import {
  useContext,
  useEffect,
  useState,
} from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Image, LoadingBubbles, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import canvaLoadingSvg from '@assets/canvaLoading.svg'

const MODAL_KEY = 'CanvaArtworkingLoadingModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

type StepItem = {
  key: 'createProjectFolder' | 'uploadAssets' | 'createArtwork',
  title: string,
  subtitle: string,
}

const stepItems: StepItem[] = [
  {
    key: 'createProjectFolder',
    title: 'Creating Project Folder',
    subtitle: 'Step 1 of 3',
  },
  {
    key: 'uploadAssets',
    title: 'Uploading Assets',
    subtitle: 'Step 2 of 3',
  },
  {
    key: 'createArtwork',
    title: 'Creating Artwork',
    subtitle: 'Step 3 of 3',
  },
]

const stepOneDelayMs = 700
const stepTwoDelayMs = 1500

const CanvaArtworkingLoadingModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const {
    modalTitle = 'Preparing',
    modalTitleSecondLine = 'Item',
    isAblySuccess = false,
  } = modalPayload

  const [autoCompletedCount, setAutoCompletedCount] = useState(0)

  useEffect(() => {
    if (!showModal){
      setAutoCompletedCount(0)
      return undefined
    }

    setAutoCompletedCount(0)

    const stepOneTimer = window.setTimeout(() => {
      setAutoCompletedCount(1)
    }, stepOneDelayMs)

    const stepTwoTimer = window.setTimeout(() => {
      setAutoCompletedCount(2)
    }, stepTwoDelayMs)

    return () => {
      window.clearTimeout(stepOneTimer)
      window.clearTimeout(stepTwoTimer)
    }
  }, [showModal])

  const processCompleted = !!isAblySuccess

  const isAutoStepCompleted = (stepKey: StepItem['key']) => {
    if (stepKey === 'createProjectFolder') return autoCompletedCount >= 1
    if (stepKey === 'uploadAssets') return autoCompletedCount >= 2
    return false
  }

  const renderStatus = (stepKey: StepItem['key']) => {
    const isCompleted = processCompleted || isAutoStepCompleted(stepKey)
    const isLoading = !processCompleted && (
      (stepKey === 'createProjectFolder' && autoCompletedCount === 0)
      || (stepKey === 'uploadAssets' && autoCompletedCount === 1)
      || (stepKey === 'createArtwork' && autoCompletedCount >= 2)
    )

    if (isCompleted){
      return (
        <Text color="green" fontSize="medium">
          <FontAwesomeIcon icon={faCheckCircle} />
        </Text>
      )
    }

    if (isLoading){
      return <LoadingBubbles color="blue" />
    }

    return (
      <Box
        border="2px solid"
        borderColor="lineColor"
        borderRadius="100%"
        height={22}
        width={22}
      />
    )
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={modalTitle} titleSecondLine={modalTitleSecondLine} />

      <SidebarModal.Content>
        <Box
          flexDirection="column"
          marginLeft="-large"
          marginRight="-large"
          marginTop="-large"
        >
          {stepItems.map((step, index) => {
            const subtitle = processCompleted || isAutoStepCompleted(step.key)
              ? `${step.subtitle} Completed`
              : step.subtitle
            const isLastStep = index === stepItems.length - 1

            return (
              <Box
                alignItems="center"
                borderBottom={isLastStep ? 0 : '1px solid'}
                borderColor="lineColor"
                key={step.key}
                padding="large"
              >
                <Box flexDirection="column" flexGrow={1}>
                  <Text fontSize="small" fontWeight="medium">
                    {step.title}
                  </Text>

                  <Text color="lightGrey" fontSize="small" marginTop="small">
                    {subtitle}
                  </Text>
                </Box>

                <Box alignItems="center" justifyContent="center" minWidth={26}>
                  {renderStatus(step.key)}
                </Box>
              </Box>
            )
          })}

          {!processCompleted && (
            <Box
              alignItems="center"
              borderBottom="1px solid"
              borderColor="lineColor"
              flexDirection="column"
              padding="xlarge"
            >
              <Image
                backgroundSize="contain"
                height={200}
                url={canvaLoadingSvg}
                width={200}
              />
            </Box>
          )}

          <Box alignItems="center" justifyContent="center" padding="large">
            <Text color="blue" fontSize="small" fontWeight="medium">
              {processCompleted ? 'Process Completed' : 'Please Wait...'}
            </Text>
          </Box>
        </Box>
      </SidebarModal.Content>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CanvaArtworkingLoadingModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
